import { CheckIcon } from "lucide-react";
import { useI18n } from "@/i18n/index.tsx";
import type { TrackSegment } from "@/lib/turns.ts";
import { cn } from "@/lib/utils.ts";

/** Research → Tariff → Decisions. Done phases show their duration; the current one is highlighted. */
export function PhaseTrack({ segments, className }: { segments: TrackSegment[]; className?: string }) {
  const { t, fmt } = useI18n();
  return (
    <ol aria-label={t.phases.trackLabel} className={cn("grid grid-cols-3 gap-2", className)}>
      {segments.map((segment) => {
        const current = segment.state === "current";
        const done = segment.state === "done";
        const detail =
          segment.durationMs !== null
            ? fmt.duration(segment.durationMs)
            : segment.state === "skipped"
              ? t.phases.skipped
              : segment.state === "missed"
                ? t.phases.missed
                : current
                ? t.phases.inProgress
                : t.phases.pending;
        return (
          <li key={segment.phase} aria-current={current ? "step" : undefined} className="flex min-w-0 flex-col gap-1.5">
            <span
              aria-hidden
              className={cn(
                "relative h-1 overflow-hidden rounded-full",
                done && "bg-primary",
                current && "bg-primary/25",
                segment.state === "pending" && "bg-border",
                (segment.state === "skipped" || segment.state === "missed") && "bg-[repeating-linear-gradient(90deg,var(--border)_0_4px,transparent_4px_7px)]",
              )}
            >
              {current ? <span className="absolute inset-y-0 left-0 w-1/2 animate-track rounded-full bg-primary" /> : null}
            </span>
            <span className="flex min-w-0 items-center gap-1 sm:gap-1.5">
              <span
                className={cn(
                  "flex size-3.5 shrink-0 items-center justify-center rounded-full",
                  done && "bg-primary text-primary-foreground",
                  current && "animate-breathe bg-primary",
                  segment.state === "pending" && "border border-border-strong",
                  (segment.state === "skipped" || segment.state === "missed") && "border border-dashed border-border-strong",
                )}
              >
                {done ? <CheckIcon className="size-2.5" strokeWidth={3} aria-hidden /> : null}
              </span>
              <span
                className={cn(
                  "truncate text-[12px] font-semibold tracking-[-0.01em] sm:text-[12.5px] sm:tracking-normal",
                  current ? "text-primary" : done ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {t.phases.track[segment.phase]}
              </span>
            </span>
            <span className={cn("code-mono truncate pl-[18px] text-[11px] sm:pl-5 sm:text-[11.5px]", current ? "text-primary/90" : "text-muted-foreground")}>
              {detail}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
